import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Flex, Image, Spinner, Text } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { getSalesOpeningEventByIdApi } from "../../api/finance.salesOpening.api";
import { resolveImageUrl } from "../../utils/format";
import { formatPrice, formatTotalApartment } from "../../utils/financeFormat";
import RichDescription from "../../components/RichDescription";
import { EMP_BASE_PATH, EMP_API_BASE_URL } from "../../config";

const TEXT = "#08142D";
const MUTED = "#6B7280";
const fmtD = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

const summaryOf = (links = []) => {
  const list = Array.isArray(links) ? links : [];
  if (!list.length) return { total: "0 căn", value: "0 tỷ" };
  const values = list.map((x) => Number(x?.apartment?.totalValue) || 0).filter((v) => v > 0);
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 0;
  return {
    total: formatTotalApartment(list.length),
    value: min && max ? `${formatPrice(min)} - ${formatPrice(max)}` : "0",
  };
};

const InfoRow = ({ label, children }) => (
  <Flex mb="12px" align="flex-start">
    <Text w="34%" pr="12px" fontSize="14px" color={TEXT} lineHeight="22px">{label}</Text>
    <Text flex="1" fontSize="14px" fontWeight={600} color={TEXT} lineHeight="22px">{children || "-"}</Text>
  </Flex>
);

const ImageSlider = ({ paths }) => {
  const ref = useRef(null);
  const drag = useRef({ down: false, x: 0, scroll: 0 });
  const imgs = paths?.length ? paths : [null];
  return (
    <Flex
      ref={ref}
      mt="16px"
      gap="8px"
      overflowX="auto"
      css={{ "::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none" }}
      cursor="grab"
      onMouseDown={(e) => { drag.current = { down: true, x: e.pageX, scroll: ref.current.scrollLeft }; }}
      onMouseMove={(e) => { if (drag.current.down) ref.current.scrollLeft = drag.current.scroll - (e.pageX - drag.current.x); }}
      onMouseUp={() => { drag.current.down = false; }}
      onMouseLeave={() => { drag.current.down = false; }}
      sx={{ userSelect: "none" }}
    >
      {imgs.map((p, i) => (
        <Image key={i} src={p ? resolveImageUrl(p, EMP_API_BASE_URL) : appImages.image_project_example} alt="" flex="0 0 auto" w={imgs.length > 1 ? "88%" : "100%"} h="180px" objectFit="cover" borderRadius="8px" draggable={false} />
      ))}
    </Flex>
  );
};

const SalesOpeningEventDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getSalesOpeningEventByIdApi(id)
      .then((d) => setData(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  const s = summaryOf(data?.apartmentSaleOpeningEventLink);
  const paths = (data?.project?.images || []).map((x) => x?.path).filter(Boolean);

  const pageBody = (
    <>

      <Box flex="1" overflowY="auto">
        {loading ? (
          <Flex justify="center" py="60px"><Spinner color={APP.primary200} /></Flex>
        ) : error || !data ? (
          <Flex justify="center" py="60px"><Text fontSize="14px" color={MUTED}>Không tìm thấy thông tin đợt mở bán</Text></Flex>
        ) : (
          <Box>
            <Text mx="16px" mt="12px" fontSize="18px" fontWeight={600} color={TEXT} noOfLines={2}>{data?.name || "-"}</Text>
            <Box px="16px"><ImageSlider paths={paths} /></Box>
            <Box px="16px" pt="16px">
              <InfoRow label="Dự án">{data?.project?.name}</InfoRow>
              <InfoRow label="Thời gian">{fmtD(data?.startTime)} - {fmtD(data?.endTime)}</InfoRow>
              <InfoRow label="Số căn hộ">{s.total}</InfoRow>
              <InfoRow label="Giá">{s.value}</InfoRow>
            </Box>
            {data?.description && (
              <Box px="16px" pt="4px" pb="16px">
                <Text fontSize="16px" fontWeight={600} color={TEXT} mb="8px">Tổng quan</Text>
                <RichDescription html={data.description} />
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Box borderTop="1px solid #EEF1F6" p="16px">
        <Flex gap="12px">
          <Button flex="1" h="48px" bg="#EC302A" color="white" borderRadius="8px" _hover={{ bg: "#EC302A", opacity: 0.92 }} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/chon-can-ho?mode=booking&eventId=${id}&projectId=${data?.project?._id || ""}`)}>
            Booking
          </Button>
          <Button flex="1" h="48px" bg="#FF9000" color="white" borderRadius="8px" _hover={{ bg: "#FF9000", opacity: 0.92 }} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/chon-can-ho?mode=locking&eventId=${id}&projectId=${data?.project?._id || ""}`)}>
            Locking
          </Button>
        </Flex>
      </Box>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Chi tiết đợt mở bán" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh" display="flex" flexDirection="column">
      <AppBar title="Chi tiết đợt mở bán" onBack={() => navigate(`${EMP_BASE_PATH}/tai-chinh/dot-mo-ban`)} />
      {pageBody}
    </Box>
  );
};

export default SalesOpeningEventDetail;
